import { Component, OnInit, Input, EventEmitter, Output, OnDestroy, SimpleChanges, OnChanges, signal, effect, WritableSignal } from '@angular/core';
import { IntegrationPushBackService } from 'src/app/services/integration-pushback.service';
import { IntegrationRunTimeService } from 'src/app/services/integration-runtime.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { ToastService } from '../../../../services/toast.service';
import { IntegrationRunTime } from 'src/app/models/integration-runtime';
import { IntegrationPushBack } from 'src/app/models/integration-pushback';
import { Integration } from 'src/app/models/integration';
import { IntegrationInformation } from 'src/app/models/integration-information';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { createDeleteButton, createEditButton, GridSort } from 'src/app/features/grid/classes/grid-config.types';
import { LTPngTableComponent } from 'src/app/features/grid/components/lt-png-table/lt-png-table.component';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  selector: 'app-integration-pushback-view',
  templateUrl: './integration-pushback-view.component.html',
  styleUrl: './integration-pushback-view.component.scss',
  imports: [
    MatExpansionModule,
    LTPngTableComponent,
  ],
})
export class IntegrationPushbackViewComponent implements OnInit, OnDestroy, OnChanges {
  //componentDestroyed$: Subject<boolean> = new Subject();
  componentDestroyed$ = new Subject<boolean>();
  @Input() integrationPushBacks: IntegrationPushBack[] = [];
  @Input() integrationRunTimes: IntegrationRunTime[] = [];
  runTimeMap: Map<string,IntegrationRunTime> = new Map<string,IntegrationRunTime>();

  // Grid data
  public readonly pushbackGridSettings = signal<GridSettings>(null);
  public readonly runTimeGridSettings = signal<GridSettings>(null);

  // Child components
  runTimePanelExpanded = false;

  // Status variables
  public dataLoading: WritableSignal<boolean> = signal<boolean>(true);
  public runTimeLoading: WritableSignal<boolean> = signal<boolean>(true);

  @Input() integration: Integration = null;
  @Input() integrations: Integration[] = [];
  @Input() integrationInformations: IntegrationInformation[] = [];
  @Output() itemToDelete = new EventEmitter<IntegrationPushBack>();
  @Output() itemToEdit = new EventEmitter<IntegrationPushBack>();
  @Input() allowDelete = true;
  @Input() allowEdit = true;
  @Input() hideIntegrationColumns = true;
  @Input() searchedByIdentifier = false;

  allIntegrationPushBacks: IntegrationPushBack[] = [];

  constructor(
    protected typeService: TypeConstantService,
    protected toastService: ToastService,
    private pushBackService: IntegrationPushBackService,
    private runTimeService: IntegrationRunTimeService,
  ) {
    this.pushbackGridSettings.set(this.setupGrid());
    this.runTimeGridSettings.set(this.setupRunTimeGrid());
  }

  setupRunTimeGrid(data: any[] = []): GridSettings {
    // Identifier (company id/name) search spans multiple integrations on one destination,
    // so lead with Integration Name and drop the redundant destination columns.
    const byIdentifier = this.searchedByIdentifier;

    const columns: GridColumnData[] = [];
    if (byIdentifier) {
      columns.push(new GridColumnData({ field: 'integrationName', header: 'Integration Name', width: "220px" }));
    }
    columns.push(
      new GridColumnData({ field: 'importTypeDescription', header: 'Import Type', width: "160px" }),
    );
    if (!byIdentifier) {
      columns.push(
        new GridColumnData({ field: 'destinationEndpointName', header: 'Destination Endpoint', width: "180px" }),
        new GridColumnData({ field: 'destinationEndpointDescription', header: 'Destination Description', width: "200px" }),
      );
    }
    columns.push(
      new GridColumnData({ field: 'lastRunTime', header: 'Last Run Time', width: "180px", type:'date', format:'yyyy-MM-dd hh:mm:ss a' }),
      new GridColumnData({ field: 'nextRunTime', header: 'Next Run Time', width: "180px", type:'date', format:'yyyy-MM-dd hh:mm:ss a' }),
      new GridColumnData({ field: 'lastDataSent', header: 'Last Data Sent', width: "180px", type:'date', format:'yyyy-MM-dd hh:mm:ss a' }),
      new GridColumnData({ field: 'activePushBack', header: 'Active PushBack', filterType: 'boolean', width: "130px" }),
    );

    const multisort: GridSort[] = byIdentifier
      ? [
          { field: 'integrationName', order: 1 },
          { field: 'importTypeDescription', order: 1 },
        ]
      : [
          { field: 'importTypeDescription', order: 1 },
        ];

    return new GridSettings({
      loading: this.runTimeLoading,
      dataKey: 'integrationRunTimeId',
      data: data,
      columns: columns,
      sortMode: 'multiple',
      filter: {
        enabled: false,
        display: 'row',
        mode: 'row',
      },
      multisort: multisort,
    });
  }

  refreshRunTimeGrid(): void {
    let runTimes: any[] = this.integrationRunTimes ?? [];

    if (this.searchedByIdentifier) {
      runTimes = runTimes.map((r) => ({
        ...r,
        integrationName: this.integrationInformations?.find(i => i.integrationId == r.integrationId)?.integrationName ?? null,
      }));
    }

    // Columns depend on inputs, so rebuild rather than only swapping data
    this.runTimeGridSettings.set(this.setupRunTimeGrid(runTimes));
    this.runTimeLoading.set(false);
  }

  setupGrid(): GridSettings {
    return new GridSettings({
      loading: this.dataLoading,
      dataKey: 'integrationPushBackId',
      data: this.allIntegrationPushBacks,
      columns: [
        new GridColumnData({ field: 'integrationName', header: 'Integration Name', width: "200px", display: !this.hideIntegrationColumns }),
        new GridColumnData({ field: 'dataSourceTypeId', header: 'Data Source Type', width: "170px", display: !this.hideIntegrationColumns }),
        new GridColumnData({ field: 'locked', header: 'Locked', filterType: 'boolean', width: "100px", display: !this.hideIntegrationColumns }),
        new GridColumnData({ field: 'serverId', header: 'Server', width: "90px", display: !this.hideIntegrationColumns }),
        new GridColumnData({ field: 'runTimeDescription', header: 'RunTime', width: "160px" }),
        new GridColumnData({ field: 'pushBackLastRunTimeDate', header: 'Last Run Time', width: "180px", type:'date', format:'yyyy-MM-dd hh:mm:ss a' }),
        new GridColumnData({ field: 'triggerDate', header: 'Trigger Date', width: "180px", type:'date', format:'yyyy-MM-dd hh:mm:ss a' }),
        new GridColumnData({ field: 'actualStartDate', header: 'Start Date', width: "180px", type:'date', format:'yyyy-MM-dd hh:mm:ss a' }),
        new GridColumnData({ field: 'pushBackCompleted', header: 'Completed Date', width: "180px", type:'date', format:'yyyy-MM-dd hh:mm:ss a' }),
        new GridColumnData({ field: 'active', header: 'Active', filterType: 'boolean', width: "100px" }),
        new GridColumnData({ field: 'jiraStoryURL', header: 'Jira Story URL', width: "180px" }),
        new GridColumnData({ field: 'comment', header: 'Comment', width: "180px", display: false }),
        new GridColumnData({ field: 'integrationId', header: 'IntegrationId', width: "100px", display: false }),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: true,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'pushBackLastRunTimeDate',
          order: -1,
        },
      ],

      commandsColumn: {
        enabled: true,
        headerText: 'Edit',
        layout: 'horizontal',
        buttonSpacing: '0.25rem',
        buttons: [
          createEditButton({
            disabled: (pushBack) => !pushBack.active
          }),
          createDeleteButton({
            disabled: (pushBack) => !pushBack.active
          }),
        ],
      },
    });
  }

  refreshGridSettings(): void {
    if (this.allIntegrationPushBacks?.length && this.pushbackGridSettings())
    {
      this.pushbackGridSettings.set(this.setupGrid());
    }
  }

  setDeletingIntegrationPushBack(event: IntegrationPushBack): void {
    if (this.allowDelete && event != null && event != undefined)
    {
      this.itemToDelete.emit(event);
    }
  }

  setEditingIntegrationPushBack(event: IntegrationPushBack): void {
    if (this.allowEdit && event != null && event != undefined)
    {
      this.itemToEdit.emit(event);
    }
  }

  ngOnInit(): void {
    this.getPushBacks();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes?.integrationPushBacks?.currentValue != null && changes?.integrationPushBacks?.firstChange != true) ||
      (changes?.integrationIdentifier?.currentValue != null && changes?.integrationIdentifier?.firstChange != true) ||
      (changes?.runTimeMap?.currentValue != null && changes?.runTimeMap?.firstChange != true) ||
      (changes?.integrationRunTimes?.currentValue != null && changes?.integrationRunTimes?.firstChange != true)
    ){
      this.getPushBacks();
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  processPushBacks(pushBacks: IntegrationPushBack[]): void {
    this.allIntegrationPushBacks = pushBacks.map((v) => {
          var runTime = this.runTimeMap.get(v.integrationRunTimeId);
          let integrationId = runTime.integrationId;
          let integrationName = null;
          let dataSourceTypeId = null;
          let locked = null;
          let serverId = null;

          if (this.integration != null)
          {
            integrationId = this.integration.integrationId;
            integrationName = this.integration.name;
            dataSourceTypeId = this.integration.dataSourceTypeId;
            serverId = this.integration.serverId;
            locked = this.integration.locked;
          }
          if (this.integrations?.length == 1)
          {
            integrationId = this.integrations[0].integrationId;
            integrationName = this.integrations[0].name;
            dataSourceTypeId = this.integrations[0].dataSourceTypeId;
            serverId = this.integrations[0].serverId;
            locked = this.integrations[0].locked;
          }
          else if (this.integrations?.length > 1)
          {
            var pushbackIntegration = this.integrations.find(int => int.integrationId == integrationId);
            integrationName = pushbackIntegration.name;
            dataSourceTypeId = pushbackIntegration.dataSourceTypeId;
            serverId = pushbackIntegration.serverId;
            locked = pushbackIntegration.locked;
          }
          else if (this.integrationInformations?.length > 0)
          {
            var pushbackIntegrationInformation = this.integrationInformations.find(int => int.integrationId == integrationId);
            integrationName = pushbackIntegrationInformation.integrationName;
            dataSourceTypeId = pushbackIntegrationInformation.dataSourceType;
            serverId = pushbackIntegrationInformation.serverId;
            locked = pushbackIntegrationInformation.locked;
          }

          return {
            ...v,
            runTimeDescription: `${runTime.importTypeDescription} - ${runTime.destinationEndpointName}`,
            integrationId: integrationId,
            integrationName: integrationName,
            dataSourceTypeId: dataSourceTypeId,
            serverId: serverId ? serverId : '0',
            locked: locked,
          };
        });
        this.dataLoading.set(false);
        this.refreshGridSettings();
  }

  getPushBacks(): void {
    this.dataLoading.set(true);
    this.runTimeLoading.set(true);
    if (this.integrationRunTimes?.length) {
      this.runTimeMap = new Map<string, IntegrationRunTime>();
      this.integrationRunTimes.forEach((r) => {
        this.runTimeMap.set(r.integrationRunTimeId, r);
      });
    }

    if (this.integrationPushBacks) {
      this.refreshRunTimeGrid();
      this.processPushBacks(this.integrationPushBacks);
      return;
    }

    this.allIntegrationPushBacks = [];

    if (this.integrations?.length && this.integration) {
      this.integrations.push(this.integration);
    }

    let integrationIds = this.integrations?.map(x => x.integrationId) || [];
    const integrationInformationIds = this.integrationInformations?.map(x => x.integrationId) || [];
    integrationIds = integrationIds.concat(integrationInformationIds);

    if (integrationIds == null || integrationIds.length == 0) {
      this.refreshRunTimeGrid();
      this.dataLoading.set(false);
      return;
    }
        
    this.runTimeService.getAllIntegrationRunTimesByIntegrationList(integrationIds)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(runTimes => {

        this.integrationRunTimes = runTimes.filter(r =>
          r.destinationEndpointName !== 'NoDestination' &&
          !r.importTypeDescription.includes('Cache'),
        );

        this.runTimeMap = new Map<string, IntegrationRunTime>();
        this.integrationRunTimes.forEach((r) => {
          this.runTimeMap.set(r.integrationRunTimeId, r);
        });

        this.refreshRunTimeGrid();

        if (this.integrationRunTimes == null || this.integrationRunTimes.length == 0) {
          this.dataLoading.set(false);
          return;
        }
          
        this.pushBackService.getAllIntegrationPushBacksByRunTimes(
            this.integrationRunTimes.map(r => r.integrationRunTimeId),
          )
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: (pushBacks) => {
              if (pushBacks?.length) {
                this.processPushBacks(pushBacks);
              }
            },
            complete: () => {
              this.dataLoading.set(false);
            }
          });
      });
  }
  

  public removePushBack(item: IntegrationPushBack): void {
    this.dataLoading.set(true);
    this.allIntegrationPushBacks.splice(this.allIntegrationPushBacks.indexOf(item), 1);
    this.refreshGridSettings();
    this.dataLoading.set(false);
  }

}
